import React, { useState, useEffect } from 'react';
import { clinicService } from '../../services/api';

// Import SVG icons
import { ReactComponent as PlusIcon } from '../../assets/plus.svg';
import { ReactComponent as CloseIcon } from '../../assets/close.svg';
import { ReactComponent as EditIcon } from '../../assets/edit.svg';
import { ReactComponent as SaveIcon } from '../../assets/save.svg';

const ApiKeysSection = ({ clinicId }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [editingApiKey, setEditingApiKey] = useState(null);
  const [secretKeyDisplay, setSecretKeyDisplay] = useState(null);
  const [newApiKey, setNewApiKey] = useState({
    name: '',
    description: '',
    permissions: {},
    expires_at: ''
  });

  // Load API keys on component mount and when showActiveOnly changes
  useEffect(() => {
    loadApiKeys();
  }, [clinicId, showActiveOnly]);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clinicService.getApiKeys(clinicId, showActiveOnly);
      setApiKeys(response.api_keys || []);
    } catch (error) {
      console.error('Error loading API keys:', error);
      setError('Failed to load API keys. Please try again.');
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!newApiKey.name.trim()) {
      setError('API key name is required');
      return;
    }

    try {
      setIsCreating(true);
      setError(null);

      const apiKeyData = {
        name: newApiKey.name.trim(),
        description: newApiKey.description.trim() || null,
        permissions: newApiKey.permissions,
        expires_at: newApiKey.expires_at || null
      };

      const response = await clinicService.createApiKey(clinicId, apiKeyData);

      // Show the secret key in a modal
      setSecretKeyDisplay({
        name: response.api_key.name,
        secret_key: response.api_key.secret_key
      });

      // Reset form
      setNewApiKey({
        name: '',
        description: '',
        permissions: {},
        expires_at: ''
      });
      setShowCreateForm(false);

      // Reload API keys
      await loadApiKeys();
    } catch (error) {
      console.error('Error creating API key:', error);
      setError(error.response?.data?.detail || 'Failed to create API key. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateApiKey = async () => {
    if (!editingApiKey.name.trim()) {
      setError('API key name is required');
      return;
    }

    try {
      setError(null);

      const updateData = {
        name: editingApiKey.name.trim(),
        description: editingApiKey.description?.trim() || null,
        expires_at: editingApiKey.expires_at || null
      };

      await clinicService.updateApiKey(clinicId, editingApiKey.id, updateData);
      setEditingApiKey(null);
      await loadApiKeys();
    } catch (error) {
      console.error('Error updating API key:', error);
      setError(error.response?.data?.detail || 'Failed to update API key. Please try again.');
    }
  };

  const handleDeleteApiKey = async (apiKeyId, apiKeyName) => {
    if (!window.confirm(`Are you sure you want to delete the API key "${apiKeyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setError(null);
      await clinicService.deleteApiKey(clinicId, apiKeyId);
      await loadApiKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
      setError(error.response?.data?.detail || 'Failed to delete API key. Please try again.');
    }
  };

  const handleDeactivateApiKey = async (apiKeyId, apiKeyName) => {
    if (!window.confirm(`Are you sure you want to deactivate the API key "${apiKeyName}"?`)) {
      return;
    }

    try {
      setError(null);
      await clinicService.deactivateApiKey(clinicId, apiKeyId);
      await loadApiKeys();
    } catch (error) {
      console.error('Error deactivating API key:', error);
      setError(error.response?.data?.detail || 'Failed to deactivate API key. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading API keys...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">API Keys</h3>
            <p className="text-sm text-gray-500 mt-1">
              Manage API keys for secure access to clinic data. Secret keys are only shown once upon creation.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700">Active only</span>
            </label>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create API Key
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="px-6 py-4 bg-red-50 border-l-4 border-red-400">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Secret Key Display Modal */}
      {secretKeyDisplay && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">API Key Created Successfully</h3>
                <button
                  onClick={() => setSecretKeyDisplay(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <CloseIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Important: Save this secret key now!
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>This secret key will not be shown again. Make sure to copy and store it securely.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">API Key Name</label>
                  <p className="mt-1 text-sm text-gray-900">{secretKeyDisplay.name}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Secret Key</label>
                  <div className="mt-1 flex">
                    <input
                      type="text"
                      value={secretKeyDisplay.secret_key}
                      readOnly
                      className="flex-1 block w-full rounded-l-md border-gray-300 bg-gray-50 text-sm font-mono"
                    />
                    <button
                      onClick={() => copyToClipboard(secretKeyDisplay.secret_key)}
                      className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm hover:bg-gray-100"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSecretKeyDisplay(null)}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  I've saved the secret key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-md font-medium text-gray-900">Create New API Key</h4>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input
                  type="text"
                  value={newApiKey.name}
                  onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="e.g., Production API Key"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Expires At (Optional)</label>
                <input
                  type="date"
                  value={newApiKey.expires_at}
                  onChange={(e) => setNewApiKey({ ...newApiKey, expires_at: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={newApiKey.description}
                onChange={(e) => setNewApiKey({ ...newApiKey, description: e.target.value })}
                rows={2}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Optional description for this API key"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateApiKey}
                disabled={isCreating}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isCreating ? 'Creating...' : 'Create API Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="px-6 py-4">
        {apiKeys.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No API keys found.</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Create your first API key
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {apiKeys.map((apiKey) => (
              <div key={apiKey.id} className="border border-gray-200 rounded-lg p-4">
                {editingApiKey && editingApiKey.id === apiKey.id ? (
                  // Edit Form
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name *</label>
                        <input
                          type="text"
                          value={editingApiKey.name}
                          onChange={(e) => setEditingApiKey({ ...editingApiKey, name: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Expires At</label>
                        <input
                          type="date"
                          value={editingApiKey.expires_at ? editingApiKey.expires_at.split('T')[0] : ''}
                          onChange={(e) => setEditingApiKey({ ...editingApiKey, expires_at: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea
                        value={editingApiKey.description || ''}
                        onChange={(e) => setEditingApiKey({ ...editingApiKey, description: e.target.value })}
                        rows={2}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      />
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setEditingApiKey(null)}
                        className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateApiKey}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        <SaveIcon className="h-4 w-4 inline mr-1" />
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display Mode
                  <div>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="text-lg font-medium text-gray-900">{apiKey.name}</h4>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            apiKey.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {apiKey.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {apiKey.algorithm}
                          </span>
                        </div>

                        {apiKey.description && (
                          <p className="text-sm text-gray-600 mt-1">{apiKey.description}</p>
                        )}

                        <div className="mt-2 text-xs text-gray-500 space-y-1">
                          <div>ID: <span className="font-mono">{apiKey.id}</span></div>
                          <div>Created: {formatDate(apiKey.created_at)}</div>
                          <div>Last Used: {formatDate(apiKey.last_used_at)}</div>
                          {apiKey.expires_at && (
                            <div>Expires: {formatDate(apiKey.expires_at)}</div>
                          )}
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingApiKey({ ...apiKey })}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title="Edit API Key"
                        >
                          <EditIcon className="h-4 w-4" />
                        </button>

                        {apiKey.is_active ? (
                          <button
                            onClick={() => handleDeactivateApiKey(apiKey.id, apiKey.name)}
                            className="px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200"
                            title="Deactivate API Key"
                          >
                            Deactivate
                          </button>
                        ) : null}

                        <button
                          onClick={() => handleDeleteApiKey(apiKey.id, apiKey.name)}
                          className="px-3 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200"
                          title="Delete API Key"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeysSection;
